// /reset-password?token=… — 以信中 opaque token 重設密碼（00 §4.6 / core §4.6）。
// POST /v3/auth/reset-password 撤銷該帳號全部 session；成功後導回登入。
// 缺 token → 無效連結提示。

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError, resetPassword } from '../../lib/api';
import { brand } from '../../lib/brand';
import { passwordMeetsPolicy, passwordPolicyError, passwordPolicyHint, policyFromApiError } from '../../lib/passwordPolicy';
import { PasswordInput } from '../../components/PasswordInput';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // 00 §2.2 同註冊規則(APP#59):bootstrap 有效政策起始,422 帶回的 server 政策覆寫刷新。
  const [policy, setPolicy] = useState(() => brand.passwordPolicy);

  if (status === 'authenticated') {
    return <Navigate to="/home" replace />;
  }

  if (!token) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
        <section className="card" style={{ width: 'min(400px, 100%)', textAlign: 'center' }}>
          <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('auth.reset.invalidTitle')}</h1>
          <p className="text-secondary">{t('auth.reset.invalidBody')}</p>
          <Link className="btn-primary" to="/forgot-password">
            {t('auth.reset.reapply')}
          </Link>
        </section>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!passwordMeetsPolicy(password, policy)) {
      setError(passwordPolicyError(policy, t));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      const serverPolicy = policyFromApiError(err);
      if (err instanceof ApiError && (err.status === 410 || err.code === 'RESET_TOKEN_EXPIRED' || err.code === 'VERIFY_TOKEN_EXPIRED')) {
        setError(t('auth.reset.expired'));
      } else if (err instanceof ApiError && err.code === 'WEAK_PASSWORD') {
        // server=最終權威:帶 policy 時以 server 值刷新提示與後續前端驗證。
        if (serverPolicy) setPolicy(serverPolicy);
        setError(passwordPolicyError(serverPolicy ?? policy, t));
      } else if (err instanceof ApiError) {
        setError(t('auth.reset.failGeneric', { msg: err.message }));
      } else {
        setError(t('auth.reset.failNetwork'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
        <section className="card" style={{ width: 'min(400px, 100%)', textAlign: 'center' }}>
          <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('auth.reset.doneTitle')}</h1>
          <p className="text-secondary">{t('auth.reset.doneBody')}</p>
          <button className="btn-primary" onClick={() => navigate('/login', { replace: true })}>
            {t('auth.toLogin')}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
      <section className="card" style={{ width: 'min(400px, 100%)' }}>
        <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-4xl)', letterSpacing: 'var(--letter-spacing-tight)' }}>{t('auth.reset.title')}</h1>
        <p className="text-secondary" style={{ marginTop: 'calc(-1 * var(--spacing-4))' }}>
          {t('auth.reset.subtitle')}
        </p>
        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--spacing-6)' }}>
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
              {t('auth.reset.newPassword')}
            </span>
            <PasswordInput autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={error !== null} aria-describedby="reset-pw-hint" />
            <span id="reset-pw-hint" className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {passwordPolicyHint(policy, t)}
            </span>
          </label>
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
              {t('auth.reset.confirmPassword')}
            </span>
            <PasswordInput autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={error !== null} />
          </label>
          {error && (
            <p className="error-text" role="alert" style={{ margin: 0 }}>
              {error}
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('auth.reset.submitBusy') : t('auth.reset.submit')}
          </button>
        </form>
      </section>
    </div>
  );
}
